import { truncate } from '../utils/string.js';
import type { ContentBlock, TextBlock } from './blocks.js';
import type { ErrorCode } from './errors.js';
import { ERROR_CODES, WrongStackError } from './errors.js';
import type { Message } from './messages.js';
import type { Tool } from './tool.js';

/**
 * Token usage for a single provider call, normalized across providers.
 *
 * Disjoint semantics: the four fields never overlap. `input` is the count
 * of FRESH input tokens (billed at the full input rate); `cacheRead` and
 * `cacheWrite` are separate cached subsets each priced at their own rate.
 * The total context the model loaded for this turn is
 * `input + (cacheRead ?? 0) + (cacheWrite ?? 0)`.
 *
 * Provider quirks normalized at the adapter layer:
 *  - Anthropic: returns `input_tokens` already disjoint from cache fields.
 *  - OpenAI / OpenAI-compatible: `prompt_tokens` is the TOTAL including
 *    cached portion; the adapter subtracts `cached_tokens` to stay disjoint.
 *  - Google: `promptTokenCount` likewise includes cache; adapter subtracts
 *    `cachedContentTokenCount`.
 *
 * Cost math and the context-fullness chip both depend on the disjoint
 * invariant — a TOTAL `input` plus a separate `cacheRead` count would bill
 * cached tokens twice and skew cache-hit-ratio reporting.
 */
export type ReasoningEffort = 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type CacheTtl = '5m' | '1h';

/**
 * Provider-agnostic response-format directive.
 *
 * - `{ type: 'text' }` — free-form text (default).
 * - `{ type: 'json_object' }` — valid JSON without a schema constraint.
 * - `{ type: 'json_schema', jsonSchema: { name, schema, strict? } }` — JSON
 *   constrained to the supplied JSON Schema. The `strict` flag is
 *   OpenAI-specific; Gemini ignores it in favour of `responseMimeType`.
 *
 * Each provider adapter maps this into its own wire format:
 *   OpenAI  → `response_format`
 *   Gemini  → `responseMimeType` + `responseSchema`
 *   Anthropic → (not yet supported; uses tools for structured output)
 */
export interface JsonSchemaSpec {
  name: string;
  /** OpenAI-specific: enable strict schema adherence. */
  strict?: boolean | undefined;
  /** The JSON Schema object describing the expected shape. */
  schema: Record<string, unknown>;
  /** Optional human-readable description (OpenAI). */
  description?: string | undefined;
}

export type ResponseFormat =
  | { type: 'text' }
  | { type: 'json_object' }
  | { type: 'json_schema'; jsonSchema: JsonSchemaSpec };

/**
 * Safety category threshold pair used by Google Gemini's `safetySettings`.
 *
 * Categories: `HARM_CATEGORY_HARASSMENT`, `HARM_CATEGORY_HATE_SPEECH`,
 * `HARM_CATEGORY_SEXUALLY_EXPLICIT`, `HARM_CATEGORY_DANGEROUS_CONTENT`.
 *
 * Thresholds: `BLOCK_NONE`, `BLOCK_ONLY_HIGH`, `BLOCK_MEDIUM_AND_ABOVE`,
 * `BLOCK_LOW_AND_ABOVE`.
 */
export interface SafetySetting {
  category: string;
  threshold: string;
}

export interface Usage {
  input: number;
  output: number;
  cacheRead?: number | undefined;
  /** Back-compat aggregate of all cache-write tokens. Prefer TTL-specific fields when present. */
  cacheWrite?: number | undefined;
  cacheWrite5m?: number | undefined;
  cacheWrite1h?: number | undefined;
}

/**
 * Effective prompt tokens loaded by the model for one request.
 *
 * Provider adapters normalize `Usage` to disjoint fields: `input` is fresh
 * full-rate tokens, `cacheRead` is cached prefix tokens, and `cacheWrite` is
 * the cache-written prefix segment. Context-window pressure cares about the
 * full prompt the model saw, not only the bill-at-full-rate slice.
 */
export function effectiveInputTokens(usage: Usage): number {
  return usage.input + (usage.cacheRead ?? 0) + (usage.cacheWrite ?? 0);
}

export interface ReasoningRequest {
  enabled?: boolean | undefined;
  effort?: ReasoningEffort | undefined;
  preserve?: boolean | undefined;
  display?: 'summarized' | 'omitted' | undefined;
}

export interface RequestCacheControl {
  ttl?: CacheTtl | undefined;
  /**
   * Provider-agnostic cache-partition key. A stable hash of the cacheable
   * system-prompt prefix (see `deriveCachePrefixKey`); requests sharing a prefix
   * share a key so provider backends route them to the same automatic-cache
   * partition. Consumed by OpenAI-family wires as `prompt_cache_key`; ignored by
   * Anthropic (which uses `ttl` + explicit `cache_control` markers).
   */
  key?: string | undefined;
  /**
   * Opt-in flag (from `ModelRuntimeCacheConfig.geminiExplicit`) telling the
   * Google provider to use explicit `cachedContents` for this request. Ignored
   * by other providers.
   */
  geminiExplicit?: boolean | undefined;
  /**
   * Resolved Gemini `cachedContents/*` resource name, injected by
   * `GoogleProvider.stream()` after it creates/reuses the cache. When present,
   * the Google wire sends `cachedContent` and OMITS the (now-cached) system
   * instruction + tool defs from the live body. Internal — never set by callers.
   */
  geminiCachedContentName?: string | undefined;
}

export interface ReasoningConfig {
  default: 'enabled' | 'disabled' | 'adaptive' | 'always_on';
  disableSupported: boolean;
  effortSupported: boolean;
  effortLevels: ReasoningEffort[];
  preserveThinking: 'unsupported' | 'optional' | 'always_on';
}

export interface Capabilities {
  tools: boolean;
  parallelTools: boolean;
  vision: boolean;
  streaming: boolean;
  promptCache: boolean;
  systemPrompt: boolean;
  jsonMode: boolean;
  reasoning: boolean;
  maxContext: number;
  /**
   * Maximum output tokens the model can produce in a single response.
   * Used as the default for `Request.maxTokens` when the caller doesn't
   * supply an explicit value — letting subagents run up to the model's
   * native ceiling instead of a fixed 8192 cap. Omit (undefined) to fall
   * back to a conservative default; populate per family in
   * `family-capabilities.ts` once you know the spec.
   */
  maxOutput?: number | undefined;
  cacheControl: 'native' | 'auto' | 'none';

  // ── Extended parameter support (optional; family defaults in CAPABILITIES_BY_FAMILY) ──

  /** Model accepts `top_k` / `topK` sampling parameter. */
  topK?: boolean | undefined;
  /** Model accepts `frequency_penalty` / `frequencyPenalty` parameter. */
  frequencyPenalty?: boolean | undefined;
  /** Model accepts `presence_penalty` / `presencePenalty` parameter. */
  presencePenalty?: boolean | undefined;
  /** Model accepts `seed` parameter for deterministic generation. */
  seed?: boolean | undefined;
  /**
   * Model accepts JSON Schema / structured-output constraints
   * (OpenAI `response_format.json_schema`, Gemini `responseMimeType`+`responseSchema`).
   * Distinct from `jsonMode` (which is just a system-prompt hint).
   */
  structuredOutput?: boolean | undefined;
  /** Model supports log-probability output (`logprobs`, `top_logprobs`). */
  logprobs?: boolean | undefined;
  /** Model supports audio input/output modality. */
  audio?: boolean | undefined;
  /** Model supports the `n` parameter for multiple completions. */
  multipleCompletions?: boolean | undefined;
}

export interface Request {
  model: string;
  system?: TextBlock[] | undefined;
  messages: Message[];
  tools?: Tool[] | undefined;
  /**
   * Cap on output tokens for this single response. Optional — when
   * omitted, the provider adapter falls back to its own
   * `capabilities.maxOutput` (which the catalog populates from
   * `ModelsDevModel.limit.output`). If neither is available, the
   * adapter applies a conservative 8192 safety net. Letting this stay
   * undefined at the call site means callers like Chimera can hand the
   * model its native output ceiling without hard-coding a number.
   */
  maxTokens?: number | undefined;
  temperature?: number | undefined;
  topP?: number | undefined;
  topK?: number | undefined;
  frequencyPenalty?: number | undefined;
  presencePenalty?: number | undefined;
  seed?: number | undefined;
  /**
   * End-user identifier for abuse monitoring and per-user rate limiting.
   * - Anthropic → `metadata.user_id`
   * - OpenAI   → `user`
   * - Gemini   → (not supported)
   */
  user?: string | undefined;
  /**
   * Number of response candidates to generate. Google Gemini supports
   * this via `generationConfig.candidateCount`. OpenAI does not have
   * an equivalent (`n` is conceptually similar but distinct).
   */
  candidateCount?: number | undefined;
  /**
   * Whether to return log probabilities for output tokens.
   * - OpenAI → `logprobs: boolean` (+ `topLogprobs: number`)
   * - Gemini → `generationConfig.logprobs: number` (how many top candidates)
   * Default undefined = no logprobs requested.
   */
  logprobs?: boolean | undefined;
  /**
   * Number of most probable tokens to return log probabilities for
   * (OpenAI `top_logprobs`). Only meaningful when `logprobs` is true.
   * Range: 0-20. Gemini ignores this (uses `logprobs` as the count).
   */
  topLogprobs?: number | undefined;
  stopSequences?: string[] | undefined;
  toolChoice?: 'auto' | 'required' | 'none' | { type: 'tool' | undefined; name: string };
  reasoning?: ReasoningRequest | undefined;
  cache?: RequestCacheControl | undefined;
  /**
   * Structured-output / response-format directive.
   * When set, the provider adapter maps this to its native response-format
   * parameter (OpenAI `response_format`, Gemini `responseMimeType`, etc.).
   * The model must advertise `capabilities.structuredOutput` for this to be
   * honoured; unsupported models will likely 400 or ignore it.
   */
  responseFormat?: ResponseFormat | undefined;
  /**
   * Safety category thresholds for filtering harmful content.
   * - Gemini → top-level `safetySettings` array with `{ category, threshold }`
   * - OpenAI → not supported (uses server-side moderation)
   * - Anthropic → not supported
   */
  safetySettings?: SafetySetting[] | undefined;
}

export type StopReason = 'end_turn' | 'tool_use' | 'max_tokens' | 'stop_sequence' | 'refusal';

export interface Response {
  content: ContentBlock[];
  stopReason: StopReason;
  usage: Usage;
  model: string;
}

export type StreamEvent =
  | { type: 'message_start'; model: string }
  | {
      type: 'content_block_start';
      kind: 'text' | 'tool_use' | 'thinking';
      id?: string | undefined;
      name?: string | undefined;
    }
  | { type: 'content_block_stop'; index: number }
  | { type: 'text_delta'; text: string }
  | { type: 'tool_use_start'; id: string; name: string }
  | { type: 'tool_use_input_delta'; id: string; partial: string }
  | { type: 'tool_use_stop'; id: string; input: unknown; providerMeta?: Record<string, unknown> }
  | { type: 'thinking_start'; providerMeta?: Record<string, unknown> }
  | { type: 'thinking_delta'; text: string }
  | { type: 'thinking_signature'; signature: string }
  | { type: 'thinking_stop' }
  | { type: 'message_stop'; stopReason: StopReason; usage: Usage };

export interface Provider {
  readonly id: string;
  readonly capabilities: Capabilities;
  /** Canonical streaming entry point. `complete()` defaults to a wrapper that
   * aggregates this stream — providers may override for non-streaming wires. */
  stream(req: Request, opts: { signal: AbortSignal }): AsyncIterable<StreamEvent>;
  complete(req: Request, opts: { signal: AbortSignal }): Promise<Response>;
}

/**
 * Structured body parsed from a provider's HTTP error response. Populated
 * best-effort: providers return JSON shaped differently (Anthropic uses
 * `{error: {type, message}}`, OpenAI uses `{error: {message, code}}`,
 * Google uses `{error: {status, message}}`), so the fields here are the
 * intersection that's usable for rendering and routing.
 */
export interface ProviderErrorBody {
  /** Provider-specific kind, e.g. "overloaded_error", "rate_limit_error", "invalid_request_error". */
  type?: string | undefined;
  /** Human-readable explanation from the provider. */
  message?: string | undefined;
  /** Provider request id, when present in the body or headers. */
  requestId?: string | undefined;
  /** Parsed Retry-After header (or equivalent body hint) in milliseconds. */
  retryAfterMs?: number | undefined;
  /** The raw response body (truncated to ~2 KB), kept for debugging. */
  raw?: string | undefined;
  /** True when `raw` was truncated; check `rawLength` for the original size. */
  truncated?: boolean | undefined;
  /** Original length of the response body in bytes, when `truncated` is true. */
  rawLength?: number | undefined;
}

/**
 * Canonical provider-failure taxonomy. Computed ONCE at error-construction
 * time (`classifyProviderError`) and carried on `ProviderError.kind` so
 * every downstream consumer — retry policy, cross-provider fallback,
 * recovery strategies, the subagent error classifier — branches on the
 * same classification instead of re-deriving it from status codes and
 * message regexes. When a new provider's error format needs special
 * handling, this module is the only place to teach it.
 */
export type ProviderErrorKind =
  | 'rate_limit' // 429 / rate_limit_error — back off (honour Retry-After), then failover
  | 'quota_exhausted' // credits/plan depleted — do not retry same route; fail over immediately
  | 'overloaded' // 529 / overloaded_error — retry with backoff, then failover
  | 'server' // other 5xx — retry same provider
  | 'timeout' // 408 request timeout
  | 'network' // status 0 — connection/DNS failure before a response arrived
  | 'stream_hang' // 599 sentinel — stream stalled mid-response (StreamHangError)
  | 'auth' // 401/403 — key invalid/expired; retrying without action is pointless
  | 'context_overflow' // 413 or an overflow-shaped 4xx — compact, don't retry as-is
  | 'content_filter' // provider refused on policy grounds — a sibling model may pass, but the `content_filter_reroute` recovery strategy owns that hop, NOT the fallback engine (which surfaces this kind)
  | 'invalid_request' // other 4xx — request is malformed; retrying won't help
  | 'unknown';

/**
 * Overflow-shaped provider messages. Union of the patterns previously
 * scattered across `error-handler.ts` and `coordinator/error-classifier.ts`
 * (which had drifted apart) — keep additions here, nowhere else.
 */
const CONTEXT_OVERFLOW_RE =
  /context.length|context.window|maximum context|max.*tokens?.*exceeded|prompt is too long|too long|exceeds the context|\btokens\b.*exceed|too many tokens|reduce the length|resulted in \d+ tokens|input.{0,12}too (?:large|long)|context_length_exceeded/i;

/** Content-policy refusals surfaced as HTTP errors (Azure/OpenAI `content_filter`, etc.). */
const CONTENT_FILTER_RE = /content.(filter|policy|moderation)|safety (system|filter)/i;
const QUOTA_EXHAUSTED_RE =
  /(?:insufficient|exhausted|depleted|exceeded|no|not enough)[-_\s]*(?:quota|credit|balance)|(?:quota|credit|balance)[-_\s]*(?:exhausted|depleted|exceeded|insufficient)|billing[_\s-]*(?:hard[_\s-]*)?limit|payment required|spending limit|plan limit|usage[-_\s]*limit[-_\s]*(?:reached|exceeded)/i;
/** "rate limit exceeded" pattern — checked against body.message only, NOT the
 *  raw JSON text, because OpenAI's `"code":"rate_limit_exceeded"` field would
 *  produce a false positive in the combined-text regex. */
const RATE_LIMIT_EXCEEDED_RE = /rate[-_\s]*limit[-_\s]*exceeded/i;

/**
 * Classify a provider HTTP failure into the canonical taxonomy from its
 * status code plus the parsed error body (and, for message-only errors
 * without a structured body, the error message itself). Pure and total —
 * always returns a kind, never throws.
 */
export function classifyProviderError(
  status: number,
  body?: ProviderErrorBody,
  message?: string,
): ProviderErrorKind {
  const type = body?.type;
  const text = [message, body?.message, type, body?.raw].filter(Boolean).join('\n');
  if (status === 0) return 'network';
  if (status === 408) return 'timeout';
  if (status === 599) return 'stream_hang';
  if (status === 402 || QUOTA_EXHAUSTED_RE.test(text)) return 'quota_exhausted';
  // Check body.message separately for "rate limit exceeded" — this pattern
  // should NOT match against body.raw because OpenAI's error response
  // includes `"code":"rate_limit_exceeded"` in the JSON, which would be a
  // false positive (it's a transient burst, not a hard limit).
  if (status === 429 && body?.message && RATE_LIMIT_EXCEEDED_RE.test(body.message)) {
    return 'quota_exhausted';
  }
  if (type === 'rate_limit_error' || status === 429) return 'rate_limit';
  if (type === 'overloaded_error' || status === 529) return 'overloaded';
  if (status >= 500) return 'server';
  if (
    type === 'authentication_error' ||
    type === 'permission_error' ||
    status === 401 ||
    status === 403
  ) {
    return 'auth';
  }
  if (type === 'content_filter' || CONTENT_FILTER_RE.test(text)) return 'content_filter';
  if (status === 413 || (status >= 400 && CONTEXT_OVERFLOW_RE.test(text))) {
    return 'context_overflow';
  }
  if (status >= 400) return 'invalid_request';
  return 'unknown';
}

/**
 * Whether a kind is worth retrying against the SAME provider/model.
 * `context_overflow` is deliberately false — the request must shrink first;
 * `auth`/`invalid_request`/`content_filter` won't improve on replay.
 *
 * Exhaustive by construction (`Record<ProviderErrorKind, …>`): adding a new
 * kind refuses to compile until it is classified here. Every kind→X mapping
 * in the codebase follows this drift-guard pattern — see also KIND_TO_CODE
 * below, DefaultRetryPolicy.maxAttempts, fallback-model shouldFallback, and
 * the coordinator's providerErrorToSubagentError.
 */
export function isRetryableKind(kind: ProviderErrorKind): boolean {
  return RETRYABLE_BY_KIND[kind];
}

const RETRYABLE_BY_KIND: Record<ProviderErrorKind, boolean> = {
  rate_limit: true,
  quota_exhausted: false,
  overloaded: true,
  server: true,
  timeout: true,
  network: true,
  stream_hang: true,
  auth: false,
  context_overflow: false,
  content_filter: false,
  invalid_request: false,
  unknown: false,
};

/**
 * Whether a kind is worth HOPPING to a different provider/model — the gate for
 * the cross-provider fallback engine (agent-loop extension AND the one-shot
 * orchestrator both branch on this ONE table, so their behavior can't drift).
 *
 * A distinct question from {@link isRetryableKind} (retry the SAME model):
 * a hop only helps for capacity/transport failures. Request-shaped failures
 * surface instead — `context_overflow` needs compaction, `content_filter` is
 * owned by the `content_filter_reroute` recovery strategy, and `auth` /
 * `invalid_request` are user-actionable and would fail identically on a hop.
 * The value set is currently identical to the retryable set, but it is kept as
 * its own table on purpose: the two answer different questions and may diverge.
 *
 * Exhaustive by construction (`Record<ProviderErrorKind, …>`) — a new kind
 * refuses to compile until it is classified here.
 */
export function isFallbackWorthy(kind: ProviderErrorKind): boolean {
  return FALLBACK_WORTHY_BY_KIND[kind];
}

const FALLBACK_WORTHY_BY_KIND: Record<ProviderErrorKind, boolean> = {
  rate_limit: true,
  quota_exhausted: true,
  overloaded: true,
  server: true,
  timeout: true,
  network: true,
  stream_hang: true,
  auth: false,
  context_overflow: false,
  content_filter: false,
  invalid_request: false,
  unknown: false,
};

export class ProviderError extends WrongStackError {
  public readonly status: number;
  public readonly retryable: boolean;
  public readonly providerId: string;
  /** Canonical failure classification — see {@link ProviderErrorKind}. */
  public readonly kind: ProviderErrorKind;
  public readonly body?: ProviderErrorBody | undefined;

  constructor(
    message: string,
    status: number,
    retryable: boolean,
    providerId: string,
    opts: {
      body?: ProviderErrorBody | undefined;
      cause?: unknown | undefined;
      /** Override the computed classification (rarely needed — tests, custom wires). */
      kind?: ProviderErrorKind | undefined;
    } = {},
  ) {
    const kind = opts.kind ?? classifyProviderError(status, opts.body, message);
    super({
      message,
      code: kindToCode(kind),
      subsystem: 'provider',
      severity: status >= 500 ? 'error' : 'warning',
      recoverable: retryable,
      context: { providerId, status },
      cause: opts.cause,
    });
    this.name = 'ProviderError';
    this.status = status;
    this.retryable = retryable;
    this.providerId = providerId;
    this.kind = kind;
    this.body = opts.body;
  }

  /**
   * Render a one-line, user-facing description. Designed for the CLI/TUI
   * status line and the agent's retry warning. Avoids dumping raw JSON
   * (which is what users see today when a 529 lands and the log message
   * includes the full `{"type":"error",...}` body).
   *
   * Examples:
   *   "minimax-coding-plan overloaded (529): High traffic detected. Upgrade for highspeed model. [req 06534785201de9c0…]"
   *   "openai rate limited (429): Retry after 12s"
   *   "anthropic invalid request (400): messages.0.role must be one of 'user'|'assistant'"
   *   "groq HTTP 500 (server error)"
   */
  override describe(): string {
    const kind = describeStatus(this.status, this.body?.type);
    const head = `${this.providerId} ${kind}`;
    const detail = this.body?.message?.trim();
    const reqId = this.body?.requestId
      ? ` [req ${this.body.requestId.slice(0, 16)}${this.body.requestId.length > 16 ? '…' : ''}]`
      : '';
    if (detail && detail.length > 0) {
      return `${head}: ${truncate(detail, 240)}${reqId}`;
    }
    return `${head}${reqId}`;
  }
}

/**
 * Belt-and-suspenders overflow detection for the recovery layer. Returns true
 * when a `ProviderError` is *shaped* like a context overflow even if its `kind`
 * says otherwise — an HTTP 413, or an overflow phrase anywhere in its message /
 * body. Gateways and proxies sometimes relabel an overflow as a generic
 * `invalid_request`/400 (or a caller constructs the error with an explicit
 * wrong `kind`); the `context_overflow_reduce` strategy uses this so those
 * still trigger compact-and-retry instead of failing terminally.
 */
export function isContextOverflowShaped(err: unknown): boolean {
  if (!(err instanceof ProviderError)) return false;
  if (err.kind === 'context_overflow' || err.status === 413) return true;
  if (err.status < 400) return false;
  const text = [err.message, err.body?.message, err.body?.type, err.body?.raw]
    .filter(Boolean)
    .join('\n');
  return CONTEXT_OVERFLOW_RE.test(text);
}

function describeStatus(status: number, type?: string): string {
  if (status === 0) return 'network error';
  if (status === 599) return `stream hang (${status})`;
  if (type === 'overloaded_error' || status === 529) return `overloaded (${status})`;
  if (type === 'rate_limit_error' || status === 429) return `rate limited (${status})`;
  if (type === 'authentication_error' || status === 401) return `auth failed (${status})`;
  if (type === 'permission_error' || status === 403) return `forbidden (${status})`;
  if (type === 'not_found_error' || status === 404) return `not found (${status})`;
  if (type === 'content_filter') return `content filtered (${status})`;
  if (type === 'invalid_request_error' || status === 400) return `invalid request (${status})`;
  if (status === 408) return `timeout (${status})`;
  if (status >= 500 && status < 600) return `HTTP ${status} (server error)`;
  if (type) return `${type} (${status})`;
  return `HTTP ${status}`;
}

/**
 * Thrown when the provider stream stops delivering data mid-response.
 * This is distinct from a network error (TCP reset, DNS failure) — the
 * connection is established and the response started, but chunks stopped
 * arriving before the stream completed.
 *
 * Status 599 is used as a sentinel to distinguish stream hangs from
 * regular HTTP errors while still flowing through ProviderError-based
 * retry and fallback infrastructure.
 */
export class StreamHangError extends ProviderError {
  /** Name of the provider that hung, e.g. "zai", "anthropic". */
  public readonly hungProviderId: string;
  /** Model that was being called when the hang occurred. */
  public readonly hungModel: string;
  /** How long (ms) we waited for the next chunk before declaring a hang. */
  public readonly hangTimeoutMs: number;
  /** How many bytes were received before the hang. */
  public readonly bytesReceived: number;
  /** Elapsed time (ms) from the start of the stream until the hang. */
  public readonly elapsedMs: number;

  constructor(opts: {
    providerId: string;
    model: string;
    hangTimeoutMs: number;
    bytesReceived: number;
    elapsedMs: number;
    cause?: unknown | undefined;
  }) {
    super(
      `Stream hang: ${opts.providerId}/${opts.model} — no data for ${opts.hangTimeoutMs}ms after ${opts.bytesReceived} bytes (${opts.elapsedMs}ms elapsed)`,
      599,
      true, // always retryable
      opts.providerId,
      {
        body: {
          message: `Stream stalled after ${opts.elapsedMs}ms, ${opts.bytesReceived} bytes received`,
        },
        cause: opts.cause,
      },
    );
    this.name = 'StreamHangError';
    this.hungProviderId = opts.providerId;
    this.hungModel = opts.model;
    this.hangTimeoutMs = opts.hangTimeoutMs;
    this.bytesReceived = opts.bytesReceived;
    this.elapsedMs = opts.elapsedMs;
  }
}

/** Exhaustive kind → ErrorCode mapping — new kinds must be added here or the
 *  file stops compiling (same drift-guard pattern as RETRYABLE_BY_KIND). */
const KIND_TO_CODE: Record<ProviderErrorKind, ErrorCode> = {
  network: ERROR_CODES.PROVIDER_NETWORK_ERROR,
  timeout: ERROR_CODES.PROVIDER_NETWORK_ERROR,
  rate_limit: ERROR_CODES.PROVIDER_RATE_LIMITED,
  quota_exhausted: ERROR_CODES.PROVIDER_RATE_LIMITED,
  auth: ERROR_CODES.PROVIDER_AUTH_FAILED,
  overloaded: ERROR_CODES.PROVIDER_OVERLOADED,
  context_overflow: ERROR_CODES.PROVIDER_CONTEXT_OVERFLOW,
  server: ERROR_CODES.PROVIDER_SERVER_ERROR,
  stream_hang: ERROR_CODES.PROVIDER_SERVER_ERROR,
  content_filter: ERROR_CODES.PROVIDER_INVALID_REQUEST,
  invalid_request: ERROR_CODES.PROVIDER_INVALID_REQUEST,
  unknown: ERROR_CODES.PROVIDER_INVALID_REQUEST,
};

function kindToCode(kind: ProviderErrorKind): ErrorCode {
  return KIND_TO_CODE[kind];
}
